import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { PublicCountryProvider } from "./contexts/PublicCountryContext";
import { TenantQueryRefresh } from "./components/tenancy/TenantQueryRefresh";
import { useUserRoles } from "./hooks/useUserRoles";
import { useSessionTracking } from "./hooks/useSessionTracking";
import { useIdleLogout } from "./hooks/useIdleLogout";
import { NavigationTracker } from "./components/NavigationTracker";
import { PWAInstallPrompt } from "./components/pwa/PWAInstallPrompt";

import { logger } from "./utils/productionLogger";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { errorReporter } from "./utils/errorReporting";
import { performanceMonitor } from "./utils/performanceMonitoring";
import { appQueryClient } from "./utils/queryConfig";
import { PerformanceProvider } from "./components/performance/PerformanceProvider";
import { isModuleEnabled } from "./config/modules";

// Immediate load for critical pages only
import Index from "./pages/Index";

// Lazy load Auth to reduce initial bundle size
const Auth = lazy(() => import("./pages/Auth"));

// Lazy load all other pages to reduce initial bundle size
const Search = lazy(() => import("./pages/Search"));
const SharedPingPage = lazy(() => import("./pages/SharedPingPage"));
const EmbedLocationMap = lazy(() => import("./pages/EmbedLocationMap"));
const UserPortal = lazy(() => import("./pages/UserPortal"));
const PartnerPortal = lazy(() => import("./pages/PartnerPortal"));
const SurveyorPortal = lazy(() => import("./pages/SurveyorPortal").then(module => ({ default: module.SurveyorPortal })));
const Admin = lazy(() => import("./pages/Admin"));
const VendorAdmin = lazy(() => import("./pages/VendorAdmin"));
const BusinessPortal = lazy(() => import("./pages/BusinessPortal"));
const LawEnforcementPortal = lazy(() => import("./pages/LawEnforcementPortal"));
const EMS = lazy(() => import("./pages/EMS"));
const EmsIncidents = lazy(() => import("./pages/EmsIncidents"));
const FireDepartmentPortal = lazy(() => import("./pages/FireDepartmentPortal"));
const DisasterManagementPortal = lazy(() => import("./pages/DisasterManagementPortal"));
const UtilityManagementPortal = lazy(() => import("./pages/UtilityManagementPortal"));
const RoadInfrastructurePortal = lazy(() => import("./pages/RoadInfrastructurePortal"));
const WasteManagementPortal = lazy(() => import("./pages/WasteManagementPortal"));
const TaxRevenuePortal = lazy(() => import("./pages/TaxRevenuePortal"));
const TaxpayerPortal = lazy(() => import("./pages/TaxpayerPortal"));
const CensusStatisticsPortal = lazy(() => import("./pages/CensusStatisticsPortal"));
const PermitLicensingEntryPage = lazy(() => import("./pages/PermitLicensingEntryPage"));
const PublicPermitApplicationPage = lazy(() => import("./pages/PublicPermitApplicationPage"));
const PublicDeliveryTrackPage = lazy(() => import("./pages/PublicDeliveryTrackPage"));
const PublicDeliveryServiceApplyPage = lazy(() => import("./pages/PublicDeliveryServiceApplyPage"));
const PublicDeliveryAgentApplyPage = lazy(() => import("./pages/PublicDeliveryAgentApplyPage"));
const PublicWasteServiceApplyPage = lazy(() => import("./pages/PublicWasteServiceApplyPage"));
const PublicWasteAgentApplyPage = lazy(() => import("./pages/PublicWasteAgentApplyPage"));
const PublicUtilityServiceApplyPage = lazy(() => import("./pages/PublicUtilityServiceApplyPage"));
const PublicUtilityAgentApplyPage = lazy(() => import("./pages/PublicUtilityAgentApplyPage"));
const PropertyManagementPortal = lazy(() => import("./pages/PropertyManagementPortal"));
const RealEstatePortal = lazy(() => import("./pages/RealEstatePortal"));
const DeliveryLogisticsPortal = lazy(() => import("./pages/DeliveryLogisticsPortal"));
const TourismHospitalityPortal = lazy(() => import("./pages/TourismHospitalityPortal"));
const PublicTourismPage = lazy(() => import("./pages/PublicTourismPage"));
const EducationServicesPortal = lazy(() => import("./pages/EducationServicesPortal"));
const HealthServicesPortal = lazy(() => import("./pages/HealthServicesPortal"));
const SocialServicesLayout = lazy(() => import("./pages/SocialServicesLayout"));
const SocialServicesHomePage = lazy(() => import("./pages/SocialServicesHomePage"));
const SocialServicesFleetPage = lazy(() => import("./pages/SocialServicesFleetPage"));
const BusinessRegistrationEntryPage = lazy(() => import("./pages/BusinessRegistrationEntryPage"));
const BusinessRegistrationAdminPortalRouter = lazy(() => import("./pages/BusinessRegistrationAdminPortalRouter"));
const CommunityMappingPortal = lazy(() => import("./pages/CommunityMappingPortal"));
const UserProjects = lazy(() => import("./pages/UserProjects"));
const PartnerProjects = lazy(() => import("./pages/PartnerProjects"));
const GenerateAddress = lazy(() => import("./pages/GenerateAddress"));
const Documentation = lazy(() => import("./pages/Documentation"));
const Support = lazy(() => import("./pages/Support"));
const FAQ = lazy(() => import("./pages/FAQ"));
const GetAppPage = lazy(() => import("./pages/GetAppPage"));
const TermsOfService = lazy(() => import("./components/legal/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./components/legal/PrivacyPolicy"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const CaptureLocation = lazy(() => import("./pages/CaptureLocation"));
const ServicesInfographic = lazy(() => import("./pages/ServicesInfographic"));

// Use the shared app-wide query client singleton (defined in queryConfig.ts)
// so `AuthProvider` (which is mounted outside `<QueryClientProvider>`) can
// also clear / invalidate the same cache on sign-in / sign-out.
const queryClient = appQueryClient;

const SELECTED_ROLE_KEY = 'qls_selected_role';

function readSelectedPortalRoleFromSession(): string | null {
  try {
    const stored = sessionStorage.getItem(SELECTED_ROLE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { role?: unknown };
    return typeof parsed?.role === 'string' && parsed.role.trim().length > 0
      ? parsed.role.trim()
      : null;
  } catch {
    return null;
  }
}

const ProtectedRoute = ({ children, allowedRoles }: { children: React.ReactNode; allowedRoles: string[] }) => {
  const { user, loading: authLoading } = useAuth();
  const { data: roles = [], isLoading: roleLoading, isFetching: rolesFetching } = useUserRoles();
  const location = useLocation();
  const selectedPortalRole = readSelectedPortalRoleFromSession();

  logger.debug('ProtectedRoute - User:', user?.id, 'Roles:', roles.map(r => r.role), 'Allowed roles:', allowedRoles, 'Loading:', authLoading, roleLoading);

  if (authLoading || roleLoading || rolesFetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    logger.debug('ProtectedRoute - No user, redirecting to auth');
    return <Navigate to="/auth" replace />;
  }

  // Honour portal picked at sign-in while /me/roles is still refetching after login.
  if (
    roles.length === 0 &&
    selectedPortalRole &&
    allowedRoles.includes(selectedPortalRole)
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500 mx-auto" />
          <p className="mt-4 text-gray-600">Loading your portal...</p>
        </div>
      </div>
    );
  }

  // Check if user has any of the allowed roles (never grant portal access when /me/roles resolved to [])
  const hasAccess =
    roles.some((r) => allowedRoles.includes(r.role)) ||
    Boolean(selectedPortalRole && allowedRoles.includes(selectedPortalRole));

  if (!hasAccess) {
    logger.debug('ProtectedRoute - Role not allowed, redirecting to home. User roles:', roles.map(r => r.role), 'Allowed:', allowedRoles);
    return <Navigate to="/" replace />;
  }

  logger.debug('ProtectedRoute - Access granted for roles:', roles.map(r => r.role));
  
  // Portal tabs removed - role selection now happens at sign-in
  return <>{children}</>;
};

// Loading component for lazy-loaded routes
const RouteLoading = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

function AppContent() {
  // Track user sessions with IP addresses
  useSessionTracking();
  // Sign out idle users (including admins) after 15 minutes
  useIdleLogout();
  
  // Initialize production monitoring
  useEffect(() => {
    if (import.meta.env.PROD) {
      performanceMonitor.monitorWebVitals();
      errorReporter.initialize();
      
      logger.info('Production monitoring initialized');
    }
  }, []);
  
  return (
    <PerformanceProvider>
      <QueryClientProvider client={queryClient}>
        <TenantQueryRefresh />
        <TooltipProvider>
          <Toaster />
          <PWAInstallPrompt />
          <BrowserRouter>
            <NavigationTracker />
            <Suspense fallback={<RouteLoading />}>
              <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/confirm" element={<Auth />} />
              <Route path="/tourism" element={<PublicTourismPage />} />
              <Route path="/permit-application" element={<PublicPermitApplicationPage />} />
              <Route path="/permit-licensing/apply" element={<PublicPermitApplicationPage />} />
              <Route path="/business-registration/*" element={<BusinessRegistrationEntryPage />} />
              <Route
                path="/permit-licensing/permit-licensing"
                element={<Navigate to="/permit-licensing/apply" replace />}
              />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/search" element={<Search />} />
              <Route path="/p/:id" element={<SharedPingPage />} />
              <Route path="/embed/location-map" element={<EmbedLocationMap />} />
              <Route path="/documentation" element={<Documentation />} />
              <Route path="/support" element={<Support />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/get-app" element={<GetAppPage />} />
              <Route path="/generate-address" element={<GenerateAddress />} />
              <Route path="/capture-location/:token" element={<CaptureLocation />} />
              <Route path="/services-infographic" element={<ServicesInfographic />} />
            
            {/* User Routes */}
            <Route path="/user/*" element={
              <ProtectedRoute allowedRoles={['user']}>
                <UserPortal />
              </ProtectedRoute>
            } />
            <Route path="/user/projects" element={
              <ProtectedRoute allowedRoles={['user']}>
                <UserProjects />
              </ProtectedRoute>
            } />
            
            {/* Partner Routes */}
            <Route path="/partner/*" element={
              <ProtectedRoute allowedRoles={['partner']}>
                <PartnerPortal />
              </ProtectedRoute>
            } />
            <Route path="/partner/projects" element={
              <ProtectedRoute allowedRoles={['partner']}>
                <PartnerProjects />
              </ProtectedRoute>
            } />
            
            {/* Surveyor Routes */}
            <Route path="/surveyor/*" element={
              <ProtectedRoute allowedRoles={['surveyor']}>
                <SurveyorPortal />
              </ProtectedRoute>
            } />
            
            {/* Business Routes - Allow both business and admin roles */}
            {isModuleEnabled('business') && (
              <Route path="/business/*" element={
                <ProtectedRoute allowedRoles={['business', 'admin']}>
                  <BusinessPortal />
                </ProtectedRoute>
              } />
            )}
            
            {/* Law Enforcement Routes - Allow law enforcement and admin roles */}
            {isModuleEnabled('lawEnforcement') && (
              <Route path="/law-enforcement/*" element={
                <ProtectedRoute allowedRoles={['law_enforcement', 'admin']}>
                  <ErrorBoundary>
                    <LawEnforcementPortal />
                  </ErrorBoundary>
                </ProtectedRoute>
              } />
            )}
            
            {/* EMS Routes - EMS roles have their own permission system */}
            {isModuleEnabled('ems') && (
              <Route path="/ems/*" element={
                <ProtectedRoute allowedRoles={['ems', 'admin']}>
                  <EMS />
                </ProtectedRoute>
              } />
            )}
            
            {/* Fire Department Routes */}
            <Route path="/fire-department/*" element={
              <ProtectedRoute allowedRoles={['fire_department', 'admin']}>
                <FireDepartmentPortal />
              </ProtectedRoute>
            } />
            
            {/* Disaster Management Routes */}
            <Route path="/disaster-management/*" element={
              <ProtectedRoute allowedRoles={['disaster_management', 'admin']}>
                <DisasterManagementPortal />
              </ProtectedRoute>
            } />
            
            {/* Utility Management — public apply as explicit children; portal only at /utility-management */}
            <Route path="/utility-management">
              <Route path="apply-service" element={<PublicUtilityServiceApplyPage />} />
              <Route path="apply-agent" element={<PublicUtilityAgentApplyPage />} />
              <Route
                index
                element={
                  <ProtectedRoute allowedRoles={['utility_management', 'utility_service_owner', 'utility_service_agent', 'admin']}>
                    <UtilityManagementPortal />
                  </ProtectedRoute>
                }
              />
            </Route>
            
            {/* Road Infrastructure Routes */}
            <Route path="/road-infrastructure/*" element={
              <ProtectedRoute allowedRoles={['road_infrastructure', 'admin']}>
                <RoadInfrastructurePortal />
              </ProtectedRoute>
            } />
            
            {/* Waste Management — public apply as explicit children; portal only at /waste-management */}
            <Route path="/waste-management">
              <Route path="apply-service" element={<PublicWasteServiceApplyPage />} />
              <Route path="apply-agent" element={<PublicWasteAgentApplyPage />} />
              <Route
                index
                element={
                  <ProtectedRoute allowedRoles={['waste_management', 'waste_service_owner', 'waste_service_agent', 'admin']}>
                    <WasteManagementPortal />
                  </ProtectedRoute>
                }
              />
            </Route>
            
            {/* Tax & Revenue Routes */}
            <Route path="/tax-revenue/*" element={
              <ProtectedRoute allowedRoles={['tax_revenue', 'admin']}>
                <TaxRevenuePortal />
              </ProtectedRoute>
            } />
            
            {/* Taxpayer Portal */}
            <Route path="/taxpayer/*" element={
              <ProtectedRoute allowedRoles={['taxpayer', 'admin']}>
                <TaxpayerPortal />
              </ProtectedRoute>
            } />
            
            {/* Census & Statistics Routes */}
            <Route path="/census-statistics/*" element={
              <ProtectedRoute allowedRoles={['census_statistics', 'admin']}>
                <CensusStatisticsPortal />
              </ProtectedRoute>
            } />
            
            {/* Permit & Licensing — public apply at /permit-licensing/apply; staff portal at /permit-licensing when signed in */}
            <Route path="/permit-licensing" element={<PermitLicensingEntryPage />} />
            
            {/* Property Management Routes */}
            <Route path="/property-management/*" element={
              <ProtectedRoute allowedRoles={['property_management', 'admin']}>
                <PropertyManagementPortal />
              </ProtectedRoute>
            } />
            
            {/* Real Estate Routes */}
            <Route path="/real-estate/*" element={
              <ProtectedRoute allowedRoles={['real_estate', 'admin']}>
                <RealEstatePortal />
              </ProtectedRoute>
            } />
            
            {/* Delivery & Logistics — public pages as explicit children; portal only at /delivery-logistics */}
            <Route path="/delivery-logistics">
              <Route path="track" element={<PublicDeliveryTrackPage />} />
              <Route path="apply-service" element={<PublicDeliveryServiceApplyPage />} />
              <Route path="apply-agent" element={<PublicDeliveryAgentApplyPage />} />
              <Route
                index
                element={
                  <ProtectedRoute allowedRoles={['delivery_logistics', 'delivery_service_owner', 'delivery_service_agent', 'admin']}>
                    <DeliveryLogisticsPortal />
                  </ProtectedRoute>
                }
              />
            </Route>
            
            {/* Tourism & Hospitality Routes */}
            <Route path="/tourism-hospitality/*" element={
              <ProtectedRoute allowedRoles={['tourism_hospitality', 'admin']}>
                <TourismHospitalityPortal />
              </ProtectedRoute>
            } />
            
            {/* Education Services Routes */}
            <Route path="/education-services/*" element={
              <ProtectedRoute allowedRoles={['education_services', 'admin']}>
                <EducationServicesPortal />
              </ProtectedRoute>
            } />
            
            {/* Health Services Routes */}
            <Route path="/health-services/*" element={
              <ProtectedRoute allowedRoles={['health_services', 'admin']}>
                <HealthServicesPortal />
              </ProtectedRoute>
            } />
            
            {/* Social Services Routes */}
            <Route path="/social-services" element={
              <ProtectedRoute allowedRoles={['social_services', 'admin']}>
                <SocialServicesLayout />
              </ProtectedRoute>
            }>
              <Route index element={<SocialServicesHomePage />} />
              <Route path="fleet" element={<SocialServicesFleetPage />} />
              <Route path="*" element={<Navigate to="/social-services" replace />} />
            </Route>
            
            {/* Business Registration — officer / registry administration */}
            <Route path="/business-registration-admin/*" element={
              <ProtectedRoute allowedRoles={['business_registration_admin', 'admin']}>
                <BusinessRegistrationAdminPortalRouter />
              </ProtectedRoute>
            } />
            
            {/* Community Mapping Routes */}
            <Route path="/community-mapping/*" element={
              <ProtectedRoute allowedRoles={['community_mapping', 'admin']}>
                <CommunityMappingPortal />
              </ProtectedRoute>
            } />
            
            {/* Vendor Admin Routes */}
            <Route path="/vendor-admin" element={
              <ProtectedRoute allowedRoles={['vendor_admin']}>
                <VendorAdmin />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin/*" element={
              <ProtectedRoute allowedRoles={['admin', 'qls_admin']}>
                <Admin />
              </ProtectedRoute>
            } />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </PerformanceProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <PublicCountryProvider>
          <AppContent />
        </PublicCountryProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
